import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { AssetResult } from 'src/app/job/models/asset.model';
import { environment } from 'src/environments/environment';
import { BaseService } from '../base/base.service';
import {
  AssetModel,
  AssetWithDuplicatesModel,
  ScannedAssetDetailModel,
  AssetSnoozeModel,
  AssetImResult,
} from 'src/app/property/models/new-asset.model';
import { AssetCategoryCount } from 'src/app/asset/models/asset-category-count.model';
import {
  AssetDetailsModel,
  AssetStatusCategories,
} from 'src/app/asset/models/asset.model';
import { EventService } from '../event.service';
import { Events } from 'src/app/shared/constants/events.constant';

import { tap } from 'rxjs';
import { CacheService } from '../cache.service';
import { PropertyAssetInvitationModel } from 'src/app/property/models/send-tenat-asset-link-email.model';
import { SendTenatToAssetLinkEmail } from 'src/app/shared/models/assets-register/send-tenat-asset-link-email.model';
import { AssetForRecommendationModel } from '../../../../property/models/new-asset.model';
import {
  SmartAssetRecommendation,
  SmartAssetRecommendationResults,
} from 'src/app/layout/models/smart-asset-recommendation';

@Injectable({
  providedIn: 'root',
})
export class AssetService extends BaseService {
  private currentAssetId: string;
  private currentVersion: string;
  private readonly urlPrefix = `${environment.agencyMgmt.urlPrefix}/asset`;
  private readonly summeryUrlPrefix = `${environment.agencyMgmt.urlPrefix}/asset-summary`;
  private editedAssetSubject$ = new BehaviorSubject<any>(null);
  editedAssetChanged$ = this.editedAssetSubject$.asObservable();
  private addAIAsset = new BehaviorSubject<any[]>(null);
  addAIAssetState$ = this.addAIAsset.asObservable();
  private assetStateUpdate = new BehaviorSubject<boolean>(false);
  assetStateUpdate$ = this.assetStateUpdate.asObservable();
  constructor(
    http: HttpClient,
    private eventService: EventService,
    private cacheService: CacheService
  ) {
    super(http);
  }

  public list(propertyId: string) {
    return this.get<AssetResult[]>(
      `${this.urlPrefix}/list?propertyId=` + propertyId
    );
  }

   public getById(assetId: string) {
    return this.get<AssetResult>(
      `${this.urlPrefix}?assetId=` + assetId
    );
  }


  public selectedAssets = new BehaviorSubject<AssetResult[]>([]);
  public saveSelection(assets: AssetResult[]) {
    this.selectedAssets.next(assets);
  }

  public clearSelection() {
    this.selectedAssets.next([]);
  }

  add(asset: AssetModel) {
    return this.post<any>(this.urlPrefix, asset);
  }

  edit(asset: AssetModel) {
    return this.patch<any>(this.urlPrefix, asset).pipe(
      tap(() => {
        this.eventService.dispatch(Events.EditAssetEvent);
      })
    );
  }

  approveAsset(assetId: string) {
    return this.patch<any>(`${this.urlPrefix}/approve`, {
      assetId: assetId,
    }).pipe(
      tap(() => {
        this.eventService.dispatch(Events.ApproveAssetEvent);
      })
    );
  }

  sentToTenantAsset(assetId: string) {
    return this.patch<any>(`${this.urlPrefix}/sentToTenant`, {
      assetId: assetId,
    }).pipe(
      tap(() => {
        this.eventService.dispatch(Events.SentToTenantAssetEvent);
      })
    );
  }

  details(assetId: string, version?: string): Observable<AssetModel> {
    if (assetId) this.currentAssetId = assetId;
    if (version) this.currentVersion = version;
    if (!this.currentAssetId && !this.currentVersion) return of();

    return this.cacheService.get(
      `${this.constructor.name}_${this.details.name}`,
      `${this.currentAssetId}${this.currentVersion}`,
      this.get<AssetModel>(`${this.urlPrefix}?assetId=${assetId}`),
      [
        Events.EditAssetEvent,
        Events.ApproveAssetEvent,
        Events.SentToTenantAssetEvent,
      ]
    );
  }

  assetWithDuplicates(assetId: string) {
    return this.get<AssetWithDuplicatesModel>(
      `${this.urlPrefix}/duplicates?assetId=${assetId}`
    );
  }

  deleteAsset(propertyId: string, assetId: string) {
    return this.delete<any>(`${this.urlPrefix}`, {
      propertyId: propertyId,
      assetId: assetId,
    }).pipe(
      tap(() => {
        this.eventService.dispatch(Events.DeleteAssetEvent);
      })
    );
  }

  getAssetCategoryCount() {
    return this.get<AssetCategoryCount>(`${this.summeryUrlPrefix}/count`);
  }

  getSelectedAssetCategory(assetCategory: AssetStatusCategories) {
    return this.get<AssetDetailsModel[]>(
      `${this.summeryUrlPrefix}/reviewed-category-assets?assetStatus=${assetCategory}`
    );
  }

  getSelectedAssetCategoryByPropery(
    assetCategory: AssetStatusCategories,
    pageNumber: number,
    pageSize: number,
    propertyId: string
  ) {
    return this.get<AssetDetailsModel[]>(
      `${this.summeryUrlPrefix}/reviewed-category-assets-by-property?AssetStatusCategory=${assetCategory}&pageNumber=${pageNumber}&pageSize=${pageSize}&propertyId=${propertyId}`
    );
  }
  sendTenatToAssetLinkEmail(
    propertyAssetInvitationModel: PropertyAssetInvitationModel
  ) {
    return this.post<any>(
      `${this.urlPrefix}/send-tenat-property-asset-invitation-link`,
      propertyAssetInvitationModel
    );
  }

  getAssetInvitationStatus(propertyId: string) {
    return this.get<PropertyAssetInvitationModel>(
      `${this.urlPrefix}/get-asset-invitation-status?PropertyId=${propertyId}`
    );
  }

  editedAssetDetails(assetDetail: any) {
    this.editedAssetSubject$.next(assetDetail);
  }

  saveScannedAssets(assets: ScannedAssetDetailModel) {
    return this.post<any>(`${this.urlPrefix}/scanned-assets`, assets);
  }

  sendTenatToAssetLink(
    propertyAssetInvitationModel: PropertyAssetInvitationModel
  ) {
    return this.patch<any>(
      `${this.urlPrefix}/asset-invitation-link`,
      propertyAssetInvitationModel
    );
  }

  getTenantType(assetId: string) {
    return this.get<SendTenatToAssetLinkEmail>(
      `${this.urlPrefix}/tenant-type?assetid=${assetId}`
    );
  }

  hasLinkToAnyOpenJobs(assetId: string) {
    return this.get<boolean>(
      `${this.urlPrefix}/has-link-to-any-open-jobs?assetid=${assetId}`
    );
  }
  updateAIAssetState(state: any[]) {
    this.addAIAsset.next(state);
  }
  getAssetForRecommendationView(assetId: string) {
    return this.get<AssetForRecommendationModel>(
      `${this.urlPrefix}/asset-for-recommendation?assetid=${assetId}`
    );
  }
  getJobAssetRecommendations(pageNumber: number) {
    return this.get<SmartAssetRecommendationResults>(
      `${this.urlPrefix}/assets-recommendations?pageNumber=${pageNumber}`
    );
  }
  snoozeAssetRecommendation(snooze: AssetSnoozeModel) {
    return this.post<any>(`${this.urlPrefix}/snooze-recommendation`, snooze);
  }
  changeRecommendationStatus(asset: SmartAssetRecommendation) {
    return this.patch<any>(`${this.urlPrefix}/change-smartasset-status`, asset);
  }
  getRecommendationsCount() {
    return this.get<number>(`${this.urlPrefix}/recommendations-count`);
  }

  sendAssetDetailsToOwners(propertyId: string) {
    let model = { propertyId: propertyId };
    return this.post<any>(`${this.urlPrefix}/send-assets-details-to-owners`, model);
  }


GetUpdatedAssetForReview(assetId: string) {
    return this.get<AssetImResult>(
      `${this.urlPrefix}/updated-asset-details-for-review?assetId=${assetId}`
    );
  }

updatedAssetApprove(assetId: string) {
    return this.post<any>(`${this.urlPrefix}/updated-asset-approve`, {
      AssetForReviewId: assetId,
    })
  }

  updateAssetState(state: boolean) {
    this.assetStateUpdate.next(state);
  }
}
